import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Table,
  message,
  Tag,
  Space,
  Select,
  InputNumber,
  Spin,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Modal,
  Input,
  Form
} from "antd";
import {
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  BookOutlined,
  EditOutlined,
  HomeOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import withRoleAccess from "../../hoc/withRoleAccess";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import CustomModal from "./CustomModal";

const { Option } = Select;
const { Title, Text } = Typography;

const CoursePage = () => {
  const { groupid } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [course, setCourse] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form] = Form.useForm();
  const [addModalVisible, setAddModalVisible] = useState(false);
  // ✨ NEW: Inline editing states
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [editedTeacherId, setEditedTeacherId] = useState(null);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editedSubjectId, setEditedSubjectId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [editedTime, setEditedTime] = useState('');
  const [editingHourId, setEditingHourId] = useState(null);
  const [editedHour, setEditedHour] = useState(null);
  // ✨ NEW: Inline editing for Classroom
  const [editingClassroomId, setEditingClassroomId] = useState(null);
  const [editedClassroomId, setEditedClassroomId] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  // Delete class states
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [classIdToDelete, setClassIdToDelete] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // Change log modal states
  const [changeLogModalOpen, setChangeLogModalOpen] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [pendingChange, setPendingChange] = useState(null);
  const [classChangeLogs, setClassChangeLogs] = useState([]);


  // Format minutes to hours:minutes
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return "0:00 hr";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${hours}:${mins.toString().padStart(2, '0')} hr`;
};


  // Time options 08:00–23:00 every 15 mins
  const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
    const hour = 8 + Math.floor(index / 4);
    const minutes = (index % 4) * 15;
    const label = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    return { label, value: label };
  });

  // Duration options 15min → 3 hours (in minutes)
  const durationOptions = Array.from({ length: 12 }, (_, index) => {
    const minutes = (index + 1) * 15;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return {
      label: `${h}h ${m.toString().padStart(2, "0")}m`,
      value: minutes,
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        const [teachersRes, subjectsRes, classroomsRes, changeLogsRes] = await Promise.all([
          axios.get(`${API_URL}/teachers`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/subjects`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/classrooms`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/class-change/class-change-logs`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setTeachers(teachersRes.data || []);
        setSubjects(subjectsRes.data || []);
        setClassrooms(classroomsRes.data || []);
        setClassChangeLogs(changeLogsRes.data || []);
      } catch (error) {
        console.error('Error fetching teachers or subjects:', error);
      }
    };
    fetchData();
  }, [API_URL]);

  const fetchCourseData = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${API_URL}/class-groups/${groupid}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCourse(res.data.course || res.data);
      setClasses(res.data.classes || []);
    } catch (err) {
      console.error("❌ [fetchCourseData] Error:", err);
      message.error("Failed to load course details.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, groupid, token]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  // Open change log modal before saving
  const openChangeLogModal = (changeData) => {
    setPendingChange(changeData);
    setChangeReason('');
    setChangeNote('');
    setChangeLogModalOpen(true);
  };

  // Calculate end time from start time + duration in minutes
  const calcEndTime = (startTime, durationMinutes) => {
    if (!startTime || !durationMinutes) return startTime || '00:00';
    return moment(startTime, 'HH:mm:ss').add(durationMinutes, 'minutes').format('HH:mm');
  };

  // Submit the pending change + log (called by CustomModal with reason, note)
  const submitChangeWithLog = async (reason, note) => {
    if (!pendingChange) return;

    const { classid, updatePayload, newValues, resetFn } = pendingChange;
    const classItem = classes.find(c => c.classid === classid);

    const originalStartTime = classItem?.schedule_time ? moment(classItem.schedule_time, 'HH:mm:ss').format('HH:mm') : '00:00';
    const originalEndTime = calcEndTime(classItem?.schedule_time, classItem?.schedule_hour);
    const newStartTime = newValues.schedule_time || originalStartTime;
    const newDuration = updatePayload.schedule_hour || classItem?.schedule_hour;
    const newEndTime = calcEndTime(newStartTime, newDuration);

    try {
      // 1. Save the actual change
      await axios.put(
        `${API_URL}/classes/${classid}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 2. Post change log
      await axios.post(`${API_URL}/class-change/class-change-logs`, {
        classid,
        teacherid: classItem?.teacher_id,
        originaldate: moment(classItem?.date).format('YYYY-MM-DD'),
        originalstarttime: originalStartTime,
        originalendtime: originalEndTime,
        newdate: newValues.date || moment(classItem?.date).format('YYYY-MM-DD'),
        newstarttime: newStartTime,
        newendtime: newEndTime,
        changereason: reason,
        note: note || `Changed: ${Object.keys(updatePayload).join(', ')}`,
        updateby: token
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (resetFn) resetFn();
      setChangeLogModalOpen(false);
      setPendingChange(null);
      fetchCourseData();
      // Refetch change logs to update the Change column
      const logsRes = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassChangeLogs(logsRes.data || []);
    } catch (error) {
      console.error("Error saving change:", error);
      message.error(error.response?.data?.message || "Failed to save change");
    }
  };

  // ✨ NEW: Inline edit handlers for Teacher
  const handleEditTeacherClick = (classid, teacherId) => {
    setEditingTeacherId(classid);
    setEditedTeacherId(teacherId);
  };

  const handleSaveTeacherClick = (classid) => {
    openChangeLogModal({
      classid,
      updatePayload: { teacher_id: editedTeacherId },
      originalValues: {},
      newValues: {},
      resetFn: () => setEditingTeacherId(null),
    });
  };

  // ✨ NEW: Inline edit handlers for Subject
  const handleEditSubjectClick = (classid, subjectId) => {
    setEditingSubjectId(classid);
    setEditedSubjectId(subjectId);
  };

  const handleSaveSubjectClick = (classid) => {
    openChangeLogModal({
      classid,
      updatePayload: { subjectid: editedSubjectId },
      originalValues: {},
      newValues: {},
      resetFn: () => setEditingSubjectId(null),
    });
  };

  // ✨ NEW: Inline edit handlers for Date
  const handleEditDateClick = (classid, date) => {
    setEditingDateId(classid);
    setEditedDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleSaveDateClick = (classid) => {
    openChangeLogModal({
      classid,
      updatePayload: { date: editedDate },
      originalValues: {},
      newValues: { date: editedDate },
      resetFn: () => setEditingDateId(null),
    });
  };

  // ✨ NEW: Inline edit handlers for Time
  const handleEditTimeClick = (classid, time) => {
    setEditingTimeId(classid);
    setEditedTime(moment(time, "HH:mm:ss").format("HH:mm"));
  };

  const handleSaveTime = (classid) => {
    openChangeLogModal({
      classid,
      updatePayload: { schedule_time: editedTime },
      originalValues: {},
      newValues: { schedule_time: editedTime },
      resetFn: () => setEditingTimeId(null),
    });
  };

  // ✨ NEW: Inline edit handlers for Duration
  const handleEditHourClick = (classid, hour) => {
    setEditingHourId(classid);
    setEditedHour(hour);
  };

  const handleSaveHour = (classid) => {
    openChangeLogModal({
      classid,
      updatePayload: { schedule_hour: editedHour },
      originalValues: {},
      newValues: {},
      resetFn: () => setEditingHourId(null),
    });
  };

  // ✨ NEW: Inline edit handlers for Classroom
  const handleEditClassroomClick = (classid, classroomId) => {
    setEditingClassroomId(classid);
    setEditedClassroomId(classroomId);
  };

  const handleSaveClassroomClick = (classid) => {
    openChangeLogModal({
      classid,
      updatePayload: { classroom_id: editedClassroomId },
      originalValues: {},
      newValues: {},
      resetFn: () => setEditingClassroomId(null),
    });
  };

  // Delete class handlers
  const handleDeleteClick = (classid) => {
    setClassIdToDelete(classid);
    setPassword('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const confirmDeleteClass = async () => {
    try {
      if (!password.trim()) {
        setPasswordError("Password is required");
        return;
      }

      await axios.delete(`${API_URL}/classes/${classIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: { password: password.trim() }
      });

      setIsPasswordModalOpen(false);
      message.success('Class has been successfully deleted.');
      fetchCourseData();
    } catch (error) {
      console.error("Delete request error:", error.response || error);
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setPasswordError("Password is required.");
            break;
          case 401:
            setPasswordError("Invalid password. Please try again.");
            break;
          case 404:
            setPasswordError("Class or user not found.");
            break;
          default:
            setPasswordError("Failed to delete class. Please try again later.");
        }
      } else {
        setPasswordError("Network error. Please check your connection.");
      }
    }
  };

    const handleAddClass = async () => {
    try {
      const values = await form.validateFields();

      const classgroup_id = Array.isArray(course.classgroup_ids)
        ? course.classgroup_ids[0]
        : course.classgroup_ids || course.classgroup_id;

      if (!classgroup_id) {
        console.error("❌ Missing classgroup_id in course object:", course);
        message.error("Missing classgroup_id — cannot add class.");
        return;
      }

      const payload = {
        subject_id: values.subject_id,
        teacher_id: values.teacher_id,
        branch_id: course.branch_id,
        schedule: [
          {
            start_date: values.start_date,
            time: values.time,
            duration: values.duration,
            recurring: values.recurring || "none",
            number_of_recurrences: values.number_of_recurrences || 1,
            day: moment(values.start_date).format("dddd"),
          },
        ],
      };

      const url = `${API_URL}/classes/add-to-course/${classgroup_id}`;
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refetch to get new class IDs, then log changes for newly added classes
      const refreshed = await axios.get(`${API_URL}/class-groups/${groupid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const refreshedClasses = refreshed.data.classes || [];
      const oldClassIds = new Set(classes.map(c => c.classid));
      const newClasses = refreshedClasses.filter(c => !oldClassIds.has(c.classid));

      // Log each newly added class
      for (const cls of newClasses) {
        const endTime = moment(cls.schedule_time, 'HH:mm:ss').add(cls.schedule_hour, 'minutes').format('HH:mm');
        await axios.post(`${API_URL}/class-change/newlogs`, {
          classid: cls.classid,
          teacherid: cls.teacher_id,
          originaldate: null,
          originalstarttime: null,
          originalendtime: null,
          newdate: moment(cls.date).format('YYYY-MM-DD'),
          newstarttime: moment(cls.schedule_time, 'HH:mm:ss').format('HH:mm'),
          newendtime: endTime,
          changereason: 'class_added',
          note: `Class added to course ${course.class_code}`,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      message.success("Class(es) added successfully");
      setAddModalVisible(false);
      form.resetFields();
      setCourse(refreshed.data.course || refreshed.data);
      setClasses(refreshedClasses);
      // Refetch change logs
      const logsRes = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassChangeLogs(logsRes.data || []);
    } catch (err) {
      console.error("❌ [handleAddClass] Error:", err);
      message.error(err.response?.data?.message || "Failed to add class");
    }
  };

  const totalDuration = classes.reduce((sum, cls) => sum + (parseInt(cls.schedule_hour) || 0), 0);

  const getChangeReasonDisplay = (reason) => {
    switch (reason) {
      case 'admin_adjust':
        return { text: 'Admin', color: '#005181' };
      case 'cancelled_by_student':
        return { text: 'CL by S.', color: 'red' };
      case 'cancelled_by_teacher':
        return { text: 'CL by T.', color: 'orange' };
      case 'class_on_hold_by_student':
        return { text: 'On hold by S.', color: 'blue' };
      case 'class_on_hold_by_school':
        return { text: 'On hold by P.', color: 'green' };
      case 'cancel_same_day':
        return { text: 'Cancel Same Day', color: '#FF4500' };
      case 'make_up':
        return { text: 'Make Up', color: '#8A2BE2' };
      case 'class_added':
        return { text: 'Class Added', color: '#52c41a' };
      case 'others':
        return { text: 'Others', color: 'darkgrey' };
      default:
        return { text: reason, color: 'black' };
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 180,
      render: (date, record) => {
        const isEditing = editingDateId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<CalendarOutlined />}
                onClick={() => handleEditDateClick(record.classid, record.date)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text strong>{moment(date).format("DD MMM YYYY")}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <input
              type="date"
              value={editedDate}
              onChange={(e) => setEditedDate(e.target.value)}
              style={{
                width: "100%",
                padding: "4px 11px",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                fontSize: "14px",
              }}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveDateClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingDateId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Day",
      dataIndex: "schedule_day",
      key: "schedule_day",
      width: 100,
      render: (day, record) => (
        <Tag color="blue">
          {day || moment(record.date).format("dddd")}
        </Tag>
      ),
    },
    {
      title: "Time",
      key: "time",
      width: 180,
      render: (_, record) => {
        const isEditing = editingTimeId === record.classid;

        if (!isEditing) {
          const start = moment(record.schedule_time, "HH:mm:ss").format("HH:mm");
          const end = moment(start, "HH:mm")
            .add(record.schedule_hour, "minutes")
            .format("HH:mm");

          return (
            <Space>
              <Button
                type="link"
                icon={<ClockCircleOutlined />}
                onClick={() => handleEditTimeClick(record.classid, record.schedule_time)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{start} - {end}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedTime}
              onChange={setEditedTime}
              style={{ width: "100%" }}
              options={timeOptions}
              showSearch
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveTime(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingTimeId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Hours",
      dataIndex: "schedule_hour",
      key: "schedule_hour",
      width: 150,
      render: (scheduleHour, record) => {
        const isEditing = editingHourId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditHourClick(record.classid, scheduleHour)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text style={{ color: '#555', fontWeight: 500 }}>
                {formatDuration(scheduleHour)}
              </Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedHour}
              onChange={setEditedHour}
              style={{ width: "100%" }}
              options={durationOptions}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveHour(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingHourId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Subject",
      dataIndex: "subject_name",
      key: "subject_name",
      width: 200,
      render: (text, record) => {
        const isEditing = editingSubjectId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<BookOutlined />}
                onClick={() => handleEditSubjectClick(record.classid, record.subject_id)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{text || "Not assigned"}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedSubjectId}
              onChange={setEditedSubjectId}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subjects.map((subject) => (
                <Option key={subject.subjectid} value={subject.subjectid}>
                  {subject.title}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveSubjectClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingSubjectId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
  {
  title: "Teacher",
  dataIndex: "teacher_names",
  key: "teacher_names",
  width: 200,
  render: (name, record) => {
    const isEditing = editingTeacherId === record.classid;

    if (!isEditing) {
      return (
        <Space>
          <Button
            type="link"
            icon={<UserOutlined />}
            onClick={() => handleEditTeacherClick(record.classid, record.teacher_id)}
            style={{ padding: 0, height: "auto" }}
          />
          <Text>{record.nickname || name || "Not assigned"}</Text>
        </Space>
      );
    }

    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          value={editedTeacherId}
          onChange={setEditedTeacherId}
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
        >
          {teachers.map((teacher) => (
            <Option
              key={teacher.userid}
              value={teacher.userid}
              label={teacher.nickname || `${teacher.firstname} ${teacher.lastname}`}
            >
              {teacher.nickname
                ? `${teacher.nickname}`
                : `${teacher.firstname} ${teacher.lastname}`}
            </Option>
          ))}
        </Select>

        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSaveTeacherClick(record.classid)}
          >
            Save
          </Button>
          <Button size="small" onClick={() => setEditingTeacherId(null)}>
            Cancel
          </Button>
        </Space>
      </Space>
    );
  },
},

    {
      title: "Classroom",
      dataIndex: "classroom_number",
      key: "classroom_number",
      width: 160,
      render: (classroomNumber, record) => {
        const isEditing = editingClassroomId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<HomeOutlined />}
                onClick={() => handleEditClassroomClick(record.classid, record.classroom_id)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{classroomNumber || "Not assigned"}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedClassroomId}
              onChange={setEditedClassroomId}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              placeholder="Select classroom"
            >
              {classrooms.map((room) => (
                <Option key={room.classroomid} value={room.classroomid}>
                  {room.number}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveClassroomClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingClassroomId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Change",
      key: "classchange",
      width: 130,
      render: (_, record) => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
        const display = changeLog
          ? getChangeReasonDisplay(changeLog.changereason)
          : { text: "-", color: "black" };
        return <span style={{ color: display.color, fontWeight: changeLog ? 500 : 'normal' }}>{display.text}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteClick(record.classid)}
          style={{ padding: 0, height: "auto" }}
        />
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f2f5' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <Header />
        <div style={{ 
          padding: "24px", 
          marginLeft: '70px', 
          marginTop: '60px',
        }}>
          <Spin spinning={loading}>
            {course && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                          <BookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                          {course.class_code}
                        </Title>
                        <Title level={4} style={{ margin: 0, color: '#666', fontWeight: 'normal' }}>
                          {course.subject_name}
                        </Title>
                      </div>
                      <Space>
                        <Tag color={course.isprivate ? "blue" : "green"} style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {course.isprivate ? "Private" : "Group"}
                        </Tag>
                        <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {course.platform}
                        </Tag>
                      </Space>
                    </div>

                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}>
                          <Statistic
                            title="Total Classes"
                            value={classes.length}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                          <Statistic
                            title="Total Hours"
                            value={formatDuration(totalDuration)}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
                          <Statistic
                            title="Period"
                            value={`${moment(course.start_date).format("DD MMM")} - ${moment(course.end_date).format("DD MMM YYYY")}`}
                            valueStyle={{ color: '#fa8c16', fontSize: '16px' }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </Space>
                </Card>
 <Card style={{ marginBottom: 24 }}>
                  <Space>
                      <Button 
                      icon={<ArrowLeftOutlined />} 
                      onClick={() => navigate('/adminpanel?tab=courses')}
                      size="large"
                    >
                      Back
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setAddModalVisible(true)}
                      size="large"
                    >
                      Add Class
                    </Button>

                  </Space>
                </Card>
                <Card 
                      title={<Text strong style={{ fontSize: '16px' }}>Class Schedule</Text>}

                    >
                  <Table
                    columns={columns}
                    dataSource={classes}
                    rowKey="classid"
                    bordered
                    pagination={{ 
                      pageSize: 100,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`
                    }}
                  />
                </Card>
              </>
            )}
          </Spin>
        </div>
      </div>

      <CustomModal
        isOpen={changeLogModalOpen}
        onClose={() => {
          setChangeLogModalOpen(false);
          setPendingChange(null);
        }}
        onSubmit={submitChangeWithLog}
        reason={changeReason}
        setReason={setChangeReason}
        note={changeNote}
        setNote={setChangeNote}
      />

      <Modal
        title="Confirm Delete"
        open={isPasswordModalOpen}
        onOk={confirmDeleteClass}
        onCancel={() => setIsPasswordModalOpen(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Enter your password to confirm deletion:</p>
        <Input.Password
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError('');
          }}
          placeholder="Enter password"
          status={passwordError ? 'error' : ''}
        />
        {passwordError && (
          <p style={{ color: '#ff4d4f', marginTop: 8, marginBottom: 0 }}>{passwordError}</p>
        )}
      </Modal>
        {/* Add Class Modal - Keep the same */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <Text strong>Add Class to Course</Text>
          </Space>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        onOk={handleAddClass}
        okText="Add Class"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="subject_id"
            label="Subject"
            rules={[{ required: true, message: "Please select a subject" }]}
          >
            <Select
              placeholder="Select subject"
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={subjects.map(sub => ({
                value: sub.subjectid,
                label: sub.title,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="teacher_id"
            label="Teacher"
            rules={[{ required: true, message: "Please select a teacher" }]}
          >
            <Select
              placeholder="Select teacher"
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={teachers.map(t => ({
                value: t.userid,
                label: `${t.firstname} ${t.lastname}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Start Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <input
              type="date"
              style={{
                width: '100%',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: 14,
              }}
              onChange={(e) => form.setFieldsValue({ start_date: e.target.value })}
            />
          </Form.Item>

          <Form.Item
            name="time"
            label="Start Time"
            rules={[{ required: true, message: "Please select start time" }]}
          >
            <Select
              placeholder="Select start time"
              showSearch
              style={{ width: '100%' }}
              options={timeOptions}
            />
          </Form.Item>

          <Form.Item
            name="duration"
            label="Hours"
            rules={[{ required: true, message: "Please select duration" }]}
          >
            <Select
              placeholder="Select duration"
              style={{ width: '100%' }}
              options={durationOptions}
            />
          </Form.Item>

          <Form.Item name="recurring" label="Recurrence" initialValue="none">
            <Select placeholder="None">
              <Option value="none">None (single class)</Option>
              <Option value="daily">Daily</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="bi-weekly">Bi-weekly</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="number_of_recurrences"
            label="Number of Occurrences"
            initialValue={1}
          >
            <InputNumber min={1} max={50} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default withRoleAccess(CoursePage, ["admin", "superadmin"]);